/**
 * SMS Customer View Flow
 *
 * This component handles SMS-based queue registration:
 * 1. Customer enters phone number
 * 2. System validates phone and checks CRM integration
 * 3. Customer selects services
 * 4. Queue is created and SMS is sent with queue number and link
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Chip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
    validatePhoneNumber,
    createSmsQueue,
    getAllServices,
    checkQueueStatus,
} from "./../../../api/api";
import useQueueUpdates from "../../../hooks/useQueueUpdates";
import ScrollButtons from "../../../components/ScrollButtons";
import { getFrontendUrl } from "../../../config/config";
import { normalizePhone } from "../../../utils/phone";
import useBranchInfo from "../../../hooks/useBranchInfo";
import BranchHeader from "./BranchHeader";
import BranchNotFound, { BranchLoading } from "./BranchNotFound";
import CustomerFooter from "./CustomerFooter";
import useCustomerFlowTexts from "./useCustomerFlowTexts";

// How long the confirmation stays up before the kiosk is ready for the next
// customer.
const AUTO_RETURN_MS = 3000;

const SmsSelectionPage = () => {
    // Phone already validated on the choice page arrives via router state,
    // letting the flow start directly at service selection.
    const location = useLocation();
    const prevalidated = location.state?.phoneNumber ? location.state : null;

    const [phoneNumber, setPhoneNumber] = useState(
        prevalidated?.phoneNumber || ""
    );
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [queueNumber, setQueueNumber] = useState(null);
    const [customer, setCustomer] = useState(prevalidated?.customer || null);
    const [smsSent, setSmsSent] = useState(false);
    // The customer already had a ticket today - we kept it and only moved
    // their new service selection onto it.
    const [existingQueue, setExistingQueue] = useState(false);
    // What the ticket actually carries now, straight from the API, so the
    // customer can see a service change landed.
    const [queueServices, setQueueServices] = useState("");
    const [smsContent, setSmsContent] = useState("");
    const [trackingUrl, setTrackingUrl] = useState("");
    const [step, setStep] = useState(prevalidated ? 2 : 1); // 1: Phone input, 2: Service selection, 3: Success
    // The confirmation screen stays open on the customer's phone while they
    // wait, so it has to follow the ticket the same way the SMS tracking page
    // does - otherwise pressing Assist changes nothing the customer can see.
    const [queueId, setQueueId] = useState(null);
    const [isBeingServed, setIsBeingServed] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        branchId,
        branchName,
        logoUrl,
        loading: branchLoading,
        error: branchError,
        withBranch,
    } = useBranchInfo();
    const texts = useCustomerFlowTexts(branchId);

    useEffect(() => {
        if (!branchId) return;

        // Load services once the branch is resolved
        const loadServices = async () => {
            try {
                const data = await getAllServices(branchId);
                setOptions(data);
            } catch (err) {
                console.error("Failed to load services", err);
                setError("Kunne ikke laste tjenester.");
            }
        };

        loadServices();
    }, [branchId]);

    const handlePhoneSubmit = async () => {
        if (!phoneNumber.trim()) {
            setError("Vennligst skriv inn telefonnummer.");
            return;
        }

        // The SMS operator drops numbers without a country code, so keep the
        // normalized form from here on.
        const msisdn = normalizePhone(phoneNumber);
        if (!msisdn) {
            setError("Ugyldig telefonnummer. Skriv f.eks. +47 123 45 678.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await validatePhoneNumber(msisdn, branchId);
            setPhoneNumber(msisdn);
            setCustomer(response.customer);
            setStep(2);
        } catch (err) {
            console.error("Phone validation failed", err);
            setError("Ugyldig telefonnummer. Prøv igjen.");
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (option) => {
        setSelectedOptions((prevSelected) => {
            const isSelected = prevSelected.some((item) => {
                if (typeof option === "object" && option.service_name) {
                    return item.service_name === option.service_name;
                }
                return item === option;
            });

            if (isSelected) {
                return prevSelected.filter((item) => {
                    if (typeof option === "object" && option.service_name) {
                        return item.service_name !== option.service_name;
                    }
                    return item !== option;
                });
            } else {
                return [...prevSelected, option];
            }
        });
    };

    const handleCreateQueue = async () => {
        if (selectedOptions.length === 0) {
            setError("Vennligst velg minst én tjeneste.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const serviceIds = selectedOptions
                .map((option) => {
                    const id = typeof option === "object" ? option.id : option;
                    return id;
                })
                .join(",");

            // Get current time from UI to ensure consistency
            const currentUITime = new Date().toISOString();
            const msisdn = normalizePhone(phoneNumber) || phoneNumber;
            const response = await createSmsQueue(
                msisdn,
                branchId,
                serviceIds,
                currentUITime
            );

            setQueueNumber(response.queue_number);
            setQueueId(response.queue_id);
            setIsBeingServed(false);
            setSmsSent(response.sms_sent !== false);
            setExistingQueue(Boolean(response.existing));
            setQueueServices(
                response.services ||
                    selectedOptions
                        .map((o) => (typeof o === "object" ? o.service_name : o))
                        .join(", ")
            );

            // Generate SMS content for testing with proper tracking URL
            const trackingUrl = `${getFrontendUrl(
                `/home/Next_customer-view?queue_id=${
                    response.queue_id
                }&queue_number=${
                    response.queue_number
                }&branch_id=${branchId}&service_ids=${serviceIds}`
            )}`;
            const smsText = `Din kønummer er ${response.queue_number} hos ${
                branchName || ""
            }. Følg køen: ${trackingUrl}`;
            setSmsContent(smsText);
            setTrackingUrl(trackingUrl);

            setStep(3);

            // Store queue data in localStorage for potential future use
            localStorage.setItem(
                "smsQueueData",
                JSON.stringify({
                    phoneNumber,
                    queueNumber: response.queue_number,
                    customer: response.customer,
                    services: selectedOptions,
                    serviceIds: serviceIds,
                })
            );
        } catch (err) {
            console.error("Queue creation failed", err);
            setError("Kunne ikke opprette kø. Prøv igjen.");
        } finally {
            setLoading(false);
        }
    };

    const handleBackToPhone = () => {
        if (prevalidated) {
            // Phone was entered on an earlier screen - return to the one the
            // customer actually came from (QR page or choice page).
            navigate(withBranch(prevalidated.returnTo || "/home/customer-choice"));
            return;
        }
        setStep(1);
        setCustomer(null);
        setSelectedOptions([]);
        setError(null);
    };

    // Back to the screen the customer registered from, ready for the next one.
    const goToRegister = () => {
        navigate(withBranch(prevalidated?.returnTo || "/home/customer-choice"));
    };

    // The ticket is final once registered - no changing services - and the
    // kiosk returns to the register page by itself. When the SMS failed the
    // customer has to note the number first, so stay put.
    const autoReturn = step === 3 && (smsSent || existingQueue);
    const goToRegisterRef = useRef(goToRegister);
    goToRegisterRef.current = goToRegister;
    useEffect(() => {
        if (!autoReturn) return;
        const timer = setTimeout(() => goToRegisterRef.current(), AUTO_RETURN_MS);
        return () => clearTimeout(timer);
    }, [autoReturn]);

    // Everything the status handlers need, in a ref so a changing service
    // selection never tears the subscription down and back up.
    const latest = useRef(null);
    latest.current = { queueNumber, selectedOptions, branchId, navigate };

    const goToThankYou = useCallback((isLost) => {
        const {
            queueNumber: nr,
            selectedOptions: services,
            branchId: branch,
            navigate: go,
        } = latest.current;

        go("/home/thank-you", {
            state: {
                queueNumber: nr,
                selectedOptions: services,
                ticketCode: "SMS",
                isDemoMode: false,
                isSmsFlow: true,
                isLost,
                // The SMS customer never logged in here, so the branch id is
                // the only thing the thank-you page can brand itself from.
                ticketData: branch
                    ? { client: { id: parseInt(branch, 10) }, ticket_code: "SMS" }
                    : null,
            },
        });
    }, []);

    // One place that turns a status into what the customer sees, so the Pusher
    // event and the fallback poll can never disagree.
    const applyQueueStatus = useCallback(
        (status) => {
            if (!status) return;
            if (status === "ASSISTED") {
                goToThankYou(false);
            } else if (status === "LOST") {
                goToThankYou(true);
            } else {
                setIsBeingServed(status === "SERVING");
            }
        },
        [goToThankYou]
    );

    const refreshQueueStatus = useCallback(async () => {
        const { queueNumber: nr, branchId: branch } = latest.current;
        if (!nr || !branch) return;

        try {
            const response = await checkQueueStatus(nr, branch);
            applyQueueStatus(response?.queue?.QueueStatus || response?.status);
        } catch (err) {
            console.error("Failed to check queue status", err);
        }
    }, [applyQueueStatus]);

    const isTracking = step === 3 && Boolean(queueNumber) && Boolean(branchId);

    // A refresh on entering the confirmation screen, so a status that changed
    // between creating the ticket and rendering is never missed.
    useEffect(() => {
        if (isTracking) refreshQueueStatus();
    }, [isTracking, refreshQueueStatus]);

    useQueueUpdates({
        branchId,
        enabled: isTracking,
        onRefresh: refreshQueueStatus,
        onQueueEvent: (queueData) => {
            const isOurQueue =
                (queueId && queueData.QueueID == queueId) ||
                (!queueId && queueData.QueueNr == latest.current.queueNumber);
            if (!isOurQueue) return;

            applyQueueStatus(queueData.QueueStatus);
        },
    });

    const renderPhoneInput = () => (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            px={2}
            sx={{ mt: { xs: 6, sm: 10 } }}
        >
            <BranchHeader
                logoUrl={logoUrl}
                branchName={branchName}
                isMobile={isMobile}
            />

            <Typography
                variant="h5"
                color="primary"
                textAlign="center"
                mb={2}
                sx={{ fontWeight: "bold" }}
            >
                📱 SMS Kø
            </Typography>

            <Typography
                variant="h6"
                color="text.secondary"
                textAlign="center"
                mb={3}
                sx={{ maxWidth: 500 }}
            >
                {texts.sms_phone_text}
            </Typography>

            <Box sx={{ width: "100%", maxWidth: 400, mb: 3 }}>
                <TextField
                    fullWidth
                    label="Mobilnummer"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+47 123 45 678"
                    variant="outlined"
                    error={!!error}
                    helperText={error}
                    sx={{ mb: 2 }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePhoneSubmit}
                    fullWidth
                    disabled={loading || !phoneNumber.trim()}
                >
                    {loading ? <CircularProgress size={24} /> : "Fortsett"}
                </Button>
            </Box>

            <Box mt={2}>
                <Button
                    variant="text"
                    onClick={() => navigate(withBranch("/home/qr-display"))}
                    sx={{ textTransform: "none" }}
                >
                    ← Tilbake til QR-kode
                </Button>
            </Box>

            <CustomerFooter />
        </Box>
    );

    const renderServiceSelection = () => (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            px={2}
            sx={{ mt: { xs: 6, sm: 10 } }}
        >
            <BranchHeader
                logoUrl={logoUrl}
                branchName={branchName}
                isMobile={isMobile}
            />

            <Typography
                variant="h5"
                color="primary"
                textAlign="center"
                mb={2}
                sx={{ fontWeight: "bold" }}
            >
                📱 Velg Tjenester
            </Typography>

            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                }}
            >
                <Typography variant="h6" color="text.secondary">
                    Mobilnummer:
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                    {phoneNumber}
                </Typography>
                {customer?.has_crm_profile && (
                    <Chip
                        label="CRM Profil Tilgjengelig"
                        color="success"
                        size="small"
                    />
                )}
            </Box>

            <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign="center"
                mb={2}
                sx={{ maxWidth: 500 }}
            >
                Velg hvilke tjenester du trenger hjelp med. Du kan velge flere.
            </Typography>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2, maxWidth: 400, width: "100%" }}
                >
                    {error}
                </Alert>
            )}

            <Box sx={{ width: "100%", maxWidth: 400 }}>
                {options.map((option, index) => {
                    console.log("Rendering option:", option, "Index:", index);
                    return (
                        <Button
                            key={index}
                            variant={
                                selectedOptions.some((item) => {
                                    if (
                                        typeof option === "object" &&
                                        option.service_name
                                    ) {
                                        return (
                                            item.service_name ===
                                            option.service_name
                                        );
                                    }
                                    return item === option;
                                })
                                    ? "contained"
                                    : "outlined"
                            }
                            fullWidth
                            sx={{ mb: 1, textTransform: "none" }}
                            onClick={() => handleSelect(option)}
                        >
                            {option.service_name || option}
                        </Button>
                    );
                })}
            </Box>

            <Box sx={{ mt: 2, width: "100%", maxWidth: 400 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateQueue}
                    fullWidth
                    disabled={selectedOptions.length === 0 || loading}
                >
                    {loading ? (
                        <CircularProgress size={24} />
                    ) : (
                        texts.sms_register_button_text
                    )}
                </Button>
            </Box>

            <Button
                variant="text"
                size="small"
                onClick={handleBackToPhone}
                sx={{ mt: 1 }}
            >
                Tilbake
            </Button>

            <CustomerFooter />
        </Box>
    );

    const renderSuccess = () => (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            px={2}
            sx={{ mt: { xs: 6, sm: 10 } }}
        >
            <BranchHeader
                logoUrl={logoUrl}
                branchName={branchName}
                isMobile={isMobile}
            />

            {/* Same label + number styling as the ticket page and the queue
                display, so the number looks the same everywhere. */}
            <Typography variant="h6" fontWeight="bold" textAlign="center">
                {existingQueue
                    ? "Køen din er oppdatert!"
                    : texts.sms_confirmation_text}
            </Typography>
            <Typography
                variant="h1"
                fontWeight="bold"
                textAlign="center"
                mb={2}
            >
                {queueNumber}
            </Typography>

            <Card sx={{ mb: 3, maxWidth: 400, width: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                    {queueServices && (
                        <Typography variant="body2" fontWeight="bold" mb={2}>
                            {queueServices}
                        </Typography>
                    )}
                    {isBeingServed && (
                        <Alert
                            severity="success"
                            sx={{
                                mb: 2,
                                fontWeight: "bold",
                                justifyContent: "center",
                            }}
                        >
                            {texts.being_served_message}
                        </Alert>
                    )}
                    {existingQueue ? (
                        <Typography variant="body2" color="text.secondary">
                            Du står allerede i køen med dette nummeret.
                            Tjenestene dine er oppdatert. Bruk lenken i SMS-en
                            du allerede har fått for å følge køen.
                        </Typography>
                    ) : smsSent ? (
                        <Typography variant="body2" color="text.secondary">
                            Sjekk SMS-en din og åpne lenken for å følge køen
                        </Typography>
                    ) : (
                        <Alert severity="warning">
                            Vi fikk ikke sendt SMS til {phoneNumber}. Noter
                            kønummeret ditt.
                        </Alert>
                    )}
                </CardContent>
            </Card>

            <Box sx={{ width: "100%", maxWidth: 400 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={goToRegister}
                    fullWidth
                >
                    {texts.sms_new_queue_button_text}
                </Button>
            </Box>

            <CustomerFooter />
        </Box>
    );

    if (branchLoading) return <BranchLoading />;
    if (branchError) return <BranchNotFound />;

    return (
        <>
            {step === 1 && renderPhoneInput()}
            {step === 2 && renderServiceSelection()}
            {step === 3 && renderSuccess()}
            <ScrollButtons />
        </>
    );
};

export default SmsSelectionPage;
